import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import QRScanner from '../components/QRScanner';
import FirmaCanvas from '../components/FirmaCanvas';
import FotoCaptura from '../components/FotoCaptura';
import api from '../utils/api';
import { agregarACola } from '../hooks/useOfflineQueue';
import { getDraft, saveDraft, deleteDraft } from '../hooks/useDrafts';
import { useAuth } from '../hooks/useAuth';
import { generarClientFormId } from '../utils/idempotency';
import { hoyChile } from '../utils/fecha';

const TIPOS_VISITA = [
  'Visita técnica programada',
  'Llamado de emergencia',
  'Reparación',
  'Revisión post-reparación',
  'Inspección',
  'Otro',
];

const ESTADOS_EQUIPO = [
  'Operativo',
  'Operativo en observación',
  'Fuera de servicio',
];

export default function FormVisita() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tecnico } = useAuth();
  const [paso, setPaso] = useState('qr');
  const [ascensor, setAscensor] = useState(null);
  const [folio, setFolio] = useState('');
  const [error, setError] = useState('');
  const [firmaTecnico, setFirmaTecnico] = useState(null);
  const [firmaCliente, setFirmaCliente] = useState(null);
  const [infoInterna, setInfoInterna] = useState('');
  const [mostrarInfoInterna, setMostrarInfoInterna] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [ofertaBorrador, setOfertaBorrador] = useState(null);
  const [presupuestosAprob, setPresupuestosAprob] = useState([]);
  const [asociarPresup, setAsociarPresup] = useState(false);

  const [form, setForm] = useState({
    fecha: hoyChile(),
    hora_ingreso: '',
    hora_salida: '',
    nombre_cliente: '',
    tipo_visita: '',
    otros_descripcion: '',
    resumen_visita: '',
    observaciones: '',
    componentes_entregados: '',
    hora_emergencia: '',
    estado_final_equipo: '',
    presupuesto_id: '',
  });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleQRResult = (asc) => {
    setAscensor(asc);
    setPaso('form');
  };

  // Navegación desde modal de borradores en Home: salta el paso QR
  useEffect(() => {
    if (location.state?.ascensor) {
      setAscensor(location.state.ascensor);
      setPaso('form');
    }
  }, []);

  useEffect(() => {
    if (!ascensor || !tecnico?.id) return;
    const draft = getDraft(tecnico.id, 'visita', ascensor.id);
    if (!draft) return;
    setOfertaBorrador(draft);
  }, [ascensor?.id, tecnico?.id]);

  // Presupuestos aprobados de la comunidad del ascensor (candidatos para asociar).
  // Falla en silencio: la asociación es opcional y no debe bloquear el comprobante.
  useEffect(() => {
    if (!ascensor?.id) return;
    api.get('/formularios/presupuestos-aprobados', { params: { ascensor_id: ascensor.id } })
      .then(r => setPresupuestosAprob(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPresupuestosAprob([]));
  }, [ascensor?.id]);

  useEffect(() => {
    if (paso !== 'enviando') return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [paso]);

  const restaurarDraft = (draft) => {
    if (draft.datos) setForm(prev => ({ ...prev, ...draft.datos }));
    if (draft.datos?.presupuesto_id) setAsociarPresup(true);
    if (draft.fotos) setFotos(draft.fotos);
    if (draft.firmaTecnico) setFirmaTecnico(draft.firmaTecnico);
    if (draft.firmaCliente) setFirmaCliente(draft.firmaCliente);
    if (draft.infoInterna !== undefined) setInfoInterna(draft.infoInterna);
    if (draft.mostrarInfoInterna !== undefined) setMostrarInfoInterna(draft.mostrarInfoInterna);
  };

  const guardarBorrador = () => {
    try {
      saveDraft(tecnico.id, {
        tipo: 'visita',
        ascensor_id: ascensor.id,
        ascensor,
        datos: { ...form },
        fotos,
        firmaTecnico,
        firmaCliente,
        infoInterna,
        mostrarInfoInterna,
        checklist: [],
      });
      alert('Borrador guardado. Puedes retomarlo en las próximas 24 horas.');
    } catch (e) {
      alert(e.message);
    }
  };

  const handleEnviar = async () => {
    if (!form.tipo_visita) { setError('Selecciona el tipo de visita.'); return; }
    if (!form.resumen_visita.trim()) { setError('El resumen de la visita es obligatorio.'); return; }
    if (!form.estado_final_equipo) { setError('Indica el estado final del equipo.'); return; }
    if (!firmaTecnico) { setError('La firma del técnico es obligatoria.'); return; }
    if (!firmaCliente) { setError('La firma del cliente es obligatoria.'); return; }
    setError('');
    setPaso('enviando');

    const payload = {
      tipo: 'visita',
      client_form_id: generarClientFormId(),
      ascensor_id: ascensor.id,
      fecha_visita: form.fecha,
      hora_ingreso: form.hora_ingreso || null,
      hora_salida: form.hora_salida || null,
      nombre_cliente: form.nombre_cliente,
      resumen_visita: form.resumen_visita,
      observaciones_generales: form.observaciones,
      tipo_visita: form.tipo_visita,
      otros_descripcion: form.otros_descripcion,
      componentes_entregados: form.componentes_entregados,
      hora_emergencia: form.hora_emergencia || null,
      estado_final_equipo: form.estado_final_equipo,
      firma_tecnico: firmaTecnico,
      firma_cliente: firmaCliente,
      info_interna: infoInterna || null,
      presupuesto_id: (asociarPresup && form.presupuesto_id) ? form.presupuesto_id : null,
      fotos: fotos.map((f, i) => ({ dataURL: f.dataURL, nombre: f.nombre, orden: i })),
      checklist: [],
    };

    try {
      const res = await api.post('/formularios', payload);
      deleteDraft(tecnico.id, 'visita', ascensor.id);
      setFolio(res.data.folio);
      setPaso('listo');
    } catch (err) {
      const esRed = !navigator.onLine || err.code === 'ERR_NETWORK' || !err.response;
      if (esRed) {
        const resultado = await agregarACola({
          tipo: 'visita',
          payload,
          ascensor: { comunidad: ascensor.comunidad, n_ascensor: ascensor.n_ascensor, torre: ascensor.torre },
        });
        if (resultado === 'ok') {
          deleteDraft(tecnico.id, 'visita', ascensor.id);
          setPaso('offline');
        } else if (resultado === 'cola_llena') {
          setError('La cola offline está llena (máx. 5 formularios). Envía los pendientes antes de continuar.');
          setPaso('form');
        } else {
          setError('Ya existe un formulario pendiente para este ascensor y fecha.');
          setPaso('form');
        }
      } else {
        setError(err.response?.data?.error || 'Error al enviar. Intenta de nuevo.');
        setPaso('form');
      }
    }
  };

  if (paso === 'qr') return (
    <div className="app-container">
      <div className="app-header">
        <button className="back-btn" onClick={() => navigate('/')}>←</button>
        <h1>Visita técnica</h1>
      </div>
      <div className="page-content">
        <QRScanner onResult={handleQRResult} onCancel={() => navigate('/')} />
      </div>
    </div>
  );

  if (paso === 'enviando') return (
    <div className="app-container">
      <div className="app-header"><h1>Enviando...</h1></div>
      <div className="page-content" style={{ textAlign: 'center', paddingTop: 48 }}>
        <div className="spinner" style={{ width: 48, height: 48, margin: '0 auto' }} />
        <p style={{ color: '#F4F7F5', fontWeight: 700, fontSize: 18, marginTop: 24, marginBottom: 8 }}>
          Enviando comprobante
        </p>
        <p style={{ color: '#8A9994', fontSize: 14, marginBottom: 24 }}>
          {ascensor?.comunidad} — Asc. {ascensor?.n_ascensor}
        </p>
        <div style={{
          background: '#1C1200', border: '1.5px solid #F59E0B',
          borderRadius: 12, padding: '16px 20px', maxWidth: 320, margin: '0 auto',
        }}>
          <p style={{ margin: 0, color: '#F59E0B', fontWeight: 700, fontSize: 15 }}>
            ⚠ No cierres esta ventana
          </p>
          <p style={{ margin: '8px 0 0', color: '#C9A84C', fontSize: 13, lineHeight: 1.5 }}>
            El formulario se está subiendo al servidor. Si cierras o minimizas la app antes de que termine, podrías perder el envío.
          </p>
        </div>
      </div>
    </div>
  );

  if (paso === 'offline') return (
    <div className="app-container">
      <div className="app-header"><h1>Guardado sin conexión</h1></div>
      <div className="page-content" style={{ textAlign: 'center', paddingTop: 40 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>📦</div>
        <h2 style={{ marginBottom: 8 }}>Formulario guardado</h2>
        <p style={{ color: '#8A9994', fontSize: 14, marginBottom: 8 }}>
          {ascensor.comunidad} — Ascensor {ascensor.n_ascensor} Torre {ascensor.torre}
        </p>
        <div style={{ background: '#1C1200', border: '1px solid #3D2B00', borderRadius: 10,
                      padding: '14px 16px', marginBottom: 28, textAlign: 'left' }}>
          <p style={{ margin: 0, fontSize: 13, color: '#F59E0B', fontWeight: 600 }}>
            Sin conexión a internet
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: '#8A9994' }}>
            El formulario quedó guardado localmente. Se enviará automáticamente en cuanto recuperes la conexión.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/pendientes')}
          style={{ marginBottom: 10 }}>
          Ver comprobantes pendientes
        </button>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Volver al inicio
        </button>
      </div>
    </div>
  );

  if (paso === 'listo') return (
    <div className="app-container">
      <div className="app-header"><h1>¡Enviado!</h1></div>
      <div className="page-content" style={{ textAlign: 'center', paddingTop: 40 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
        <h2 style={{ marginBottom: 8 }}>Visita registrada</h2>
        <p style={{ color: '#8A9994', marginBottom: 8 }}>
          Folio: <strong style={{ color: '#F4F7F5' }}>{folio}</strong>
        </p>
        <p style={{ color: '#8A9994', fontSize: 14, marginBottom: 32 }}>
          {ascensor.comunidad} — Ascensor {ascensor.n_ascensor} Torre {ascensor.torre}
        </p>
        <button className="btn btn-primary" onClick={() => navigate('/')}>
          Volver al inicio
        </button>
      </div>
    </div>
  );

  const FormSection = ({ title }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      marginTop: 28, marginBottom: 16,
    }}>
      <div style={{ height: 1, flex: 1, background: '#1E2922' }} />
      <span style={{
        fontSize: 11, fontWeight: 700, color: '#4A6058',
        textTransform: 'uppercase', letterSpacing: '1px', whiteSpace: 'nowrap',
      }}>{title}</span>
      <div style={{ height: 1, flex: 1, background: '#1E2922' }} />
    </div>
  );

  return (
    <div className="app-container">
      <div className="app-header">
        <button className="back-btn" onClick={() => setPaso('qr')}>←</button>
        <h1>Visita técnica</h1>
      </div>
      <div className="page-content">

        <div className="ascensor-card">
          <div className="asc-title">{ascensor.comunidad}</div>
          <div className="asc-detail">📍 {ascensor.direccion}, {ascensor.comuna}</div>
          <div className="asc-detail">🛗 Ascensor N° {ascensor.n_ascensor} — Torre {ascensor.torre}</div>
        </div>

        {ofertaBorrador && (
          <div style={{ background: '#1C1200', border: '1px solid #F59E0B', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#F59E0B', fontSize: 14 }}>
              Tienes un borrador guardado hace {Math.round((Date.now() - ofertaBorrador.saved_at) / 60000)} min
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { restaurarDraft(ofertaBorrador); setOfertaBorrador(null); }} style={{ background: '#F59E0B', color: '#000', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontWeight: 600 }}>Retomar</button>
              <button onClick={() => { deleteDraft(tecnico.id, 'visita', ascensor.id); setOfertaBorrador(null); }} style={{ background: 'transparent', color: '#F59E0B', border: '1px solid #F59E0B', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>Ignorar</button>
            </div>
          </div>
        )}

        <FormSection title="Datos de la visita" />

        <div className="form-group">
          <label>Fecha</label>
          <input type="date" value={form.fecha} onChange={e => set('fecha', e.target.value)} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label>Hora ingreso</label>
            <input type="time" value={form.hora_ingreso}
              onChange={e => set('hora_ingreso', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Hora salida</label>
            <input type="time" value={form.hora_salida}
              onChange={e => set('hora_salida', e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label>Tipo de visita *</label>
          <select value={form.tipo_visita} onChange={e => set('tipo_visita', e.target.value)}>
            <option value="">Seleccionar...</option>
            {TIPOS_VISITA.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {form.tipo_visita === 'Otro' && (
          <div className="form-group">
            <label>Descripción</label>
            <input value={form.otros_descripcion}
              onChange={e => set('otros_descripcion', e.target.value)}
              placeholder="Describe el motivo..." />
          </div>
        )}

        {form.tipo_visita === 'Llamado de emergencia' && (
          <div className="form-group">
            <label>Hora de emergencia</label>
            <input type="time" value={form.hora_emergencia}
              onChange={e => set('hora_emergencia', e.target.value)} />
          </div>
        )}

        <FormSection title="Trabajo realizado" />

        {presupuestosAprob.length > 0 && (
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={asociarPresup}
                onChange={e => {
                  setAsociarPresup(e.target.checked);
                  if (!e.target.checked) set('presupuesto_id', '');
                }}
                style={{ width: 18, height: 18, accentColor: '#0f6e56' }}
              />
              Asociar a un presupuesto aprobado
            </label>
            {asociarPresup && (
              <select value={form.presupuesto_id}
                onChange={e => set('presupuesto_id', e.target.value)}
                style={{ marginTop: 10 }}>
                <option value="">Seleccionar presupuesto...</option>
                {presupuestosAprob.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.numero}{p.nombre ? ` — ${p.nombre}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="form-group">
          <label>Resumen de la visita *</label>
          <textarea value={form.resumen_visita}
            onChange={e => set('resumen_visita', e.target.value)}
            placeholder="Describe el trabajo realizado..." rows={4} />
        </div>

        <div className="form-group">
          <label>Observaciones</label>
          <textarea value={form.observaciones}
            onChange={e => set('observaciones', e.target.value)}
            placeholder="Observaciones adicionales..." />
        </div>

        <div className="form-group">
          <label>Componentes entregados / repuestos</label>
          <input value={form.componentes_entregados}
            onChange={e => set('componentes_entregados', e.target.value)}
            placeholder="Ej: Fusible 10A, sensor de piso..." />
        </div>

        <FormSection title="Cierre" />

        <div className="form-group">
          <label>Estado final del equipo *</label>
          <select value={form.estado_final_equipo}
            onChange={e => set('estado_final_equipo', e.target.value)}>
            <option value="">Seleccionar...</option>
            {ESTADOS_EQUIPO.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label>Nombre del cliente / representante</label>
          <input value={form.nombre_cliente}
            onChange={e => set('nombre_cliente', e.target.value)}
            placeholder="Nombre de quien recibe" />
        </div>

        <FormSection title="Evidencia fotográfica" />
        <FotoCaptura maxFotos={9} onChange={setFotos} />

        <div style={{ marginTop: 24 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10,
                          cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#8A9994' }}>
            <input
              type="checkbox"
              checked={mostrarInfoInterna}
              onChange={e => setMostrarInfoInterna(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: '#0f6e56' }}
            />
            Agregar nota interna (solo supervisores)
          </label>
          {mostrarInfoInterna && (
            <textarea
              value={infoInterna}
              onChange={e => setInfoInterna(e.target.value)}
              placeholder="Información interna para supervisores — no aparece en el comprobante del cliente..."
              style={{ width: '100%', marginTop: 10, padding: '10px 12px',
                       border: '1.5px solid #e65100', borderRadius: 10,
                       fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
                       minHeight: 80, background: '#1A0D00', color: '#F4F7F5' }}
            />
          )}
        </div>

        <FormSection title="Firmas" />
        <FirmaCanvas label="Firma del técnico *" onChange={setFirmaTecnico} />
        <FirmaCanvas label="Firma del cliente (opcional)" onChange={setFirmaCliente} />

        {error && (
          <div style={{ background: '#2D0F0F', color: '#EF4444', borderRadius: 10,
                        padding: '12px 14px', fontSize: 13, marginBottom: 16,
                        border: '1px solid #3D1515' }}>
            {error}
          </div>
        )}

        <button type="button" onClick={guardarBorrador} style={{ background: '#1C1200', color: '#F59E0B', border: '1px solid #F59E0B', borderRadius: 8, padding: '12px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 15, width: '100%', marginBottom: 10 }}>
          Guardar borrador
        </button>

        <button className="btn btn-primary" onClick={handleEnviar} style={{ marginBottom: 32 }}>
          Finalizar y enviar
        </button>
      </div>
    </div>
  );
}
